import { PropsWithChildren, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './client-style.css';
import './App.css';
import { INITIAL_PAYMENT, ROUTES } from './constants';
import {
  endpointInstance,
  formatCardNumber,
  formatExpiry,
  formatPhoneInput,
  onlyDigits,
  validatePhone,
} from './methods/Methods';
import { PaymentData, TopUpPackage } from './types/common';
import ErrorView from './views/ErrorView';
import Landing from './views/Landing';
import PayWithCard from './views/PayWithCard';
import Payments from './views/Payments';
import Recharges from './views/Recharges';

const consultaLinks = [
  { href: 'https://megamovil.mx/planes', label: 'Planes' },
  { href: 'https://megamovil.mx/roaming-internacional', label: 'Planes Internacionales' },
  { href: 'https://megamovil.mx/recargas', label: 'Recargas', active: true },
  { href: 'https://megamovil.mx/compatibilidad', label: 'Compatibilidad' },
  { href: 'https://megamovil.mx/beneficios', label: 'Beneficios' },
  { href: 'https://megamovil.mx/home#d-home-cobertura', label: 'Cobertura' },
  { href: 'https://registro.megamovil.mx/vinculatulinea/', label: 'Vincula tu línea' },
];

const ayudaLinks = [
  { href: 'https://registro.megamovil.mx/vinculatulinea/', label: 'Vincula tu línea' },
  { href: 'https://megamovil.mx/preguntas-frecuentes', label: 'Preguntas frecuentes' },
  { href: 'https://megamovil.mx/soporte', label: 'Soporte' },
];

function PortalLayout({ children }: PropsWithChildren) {
  return (
    <main className="container-fluid p-0 m-0 d-flex justify-content-center align-items-center general-container-rdm">
      <section className="container">{children}</section>
    </main>
  );
}

function PortalNavigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<'consulta' | 'ayuda' | null>(null);

  const toggleDropdown = (name: 'consulta' | 'ayuda') => {
    setOpenDropdown((current) => (current === name ? null : name));
  };

  const closeAll = () => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  return (
    <>
      <div className="top-bar">
        <div className="ml-5 container d-flex justify-content-center align-items-center">
          <div className="top-bar-logo d-flex align-items-center">
            <span>La Telefonía Celular de</span>
            <img src="/assets/logo-mega.png" alt="Mega" className="mega-logo" />
          </div>
        </div>
      </div>

      <nav className="navbar navbar-expand-lg mega-navbar">
        <div className="container">
          <a className="navbar-brand d-flex align-items-center" target="_blank" rel="noreferrer" href="https://megamovil.mx/">
            <img src="/assets/logo-megamovil-wh.svg" alt="Mega móvil" />
          </a>

          <button
            className="navbar-toggler"
            type="button"
            aria-controls="megaNavbar"
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation"
            onClick={() => setIsMenuOpen((current) => !current)}
          >
            <span className="navbar-toggler-icon" />
          </button>

          <div className={`collapse navbar-collapse justify-content-end ${isMenuOpen ? 'show' : ''}`} id="megaNavbar">
            <ul className="navbar-nav align-items-center">
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle"
                  href="/"
                  id="consultaDropdown"
                  role="button"
                  aria-haspopup="true"
                  aria-expanded={openDropdown === 'consulta'}
                  onClick={(event) => {
                    event.preventDefault();
                    toggleDropdown('consulta');
                  }}
                >
                  Consulta
                </a>
                <div className={`dropdown-menu mega-dropdown ${openDropdown === 'consulta' ? 'show' : ''}`} aria-labelledby="consultaDropdown">
                  {consultaLinks.map((item) => (
                    <a
                      key={item.href}
                      className={`dropdown-item ${item.active ? 'active-item' : ''}`}
                      target="_blank"
                      rel="noreferrer"
                      href={item.href}
                      onClick={closeAll}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </li>

              <li className="nav-item">
                <a
                  className="nav-link"
                  target="_blank"
                  rel="noreferrer"
                  href="https://megamovil.mx/cambiate-a-megamovil"
                  onClick={closeAll}
                >
                  Cámbiate a Mega móvil
                </a>
              </li>

              <li className="nav-item">
                <a
                  className="nav-link"
                  target="_blank"
                  rel="noreferrer"
                  href="https://megamovil.mx/promociones"
                  onClick={closeAll}
                >
                  Promociones
                </a>
              </li>

              <li className="nav-item">
                <a className="nav-link" target="_blank" rel="noreferrer" href="https://megamovil.mx/esim" onClick={closeAll}>
                  eSIM
                </a>
              </li>

              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle"
                  href="/"
                  id="ayudaDropdown"
                  role="button"
                  aria-haspopup="true"
                  aria-expanded={openDropdown === 'ayuda'}
                  onClick={(event) => {
                    event.preventDefault();
                    toggleDropdown('ayuda');
                  }}
                >
                  Ayuda
                </a>
                <div
                  className={`dropdown-menu mega-dropdown dropdown-menu-right ${openDropdown === 'ayuda' ? 'show' : ''}`}
                  aria-labelledby="ayudaDropdown"
                >
                  {ayudaLinks.map((item) => (
                    <a
                      key={item.href}
                      className="dropdown-item"
                      target="_blank"
                      rel="noreferrer"
                      href={item.href}
                      onClick={closeAll}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </>
  );
}

function App() {
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [selectedPackage, setSelectedPackage] = useState<TopUpPackage | null>(null);
  const [packages, setPackages] = useState<TopUpPackage[]>([]);
  const [payment, setPayment] = useState<PaymentData>(INITIAL_PAYMENT);
  const [topUpNumber, setTopUpNumber] = useState('');
  const [topUpDate, setTopUpDate] = useState('');
  const call = endpointInstance;

  const onPhoneChange = (value: string) => {
    setPhone(formatPhoneInput(value));
    setSelectedPackage(null);
    setPackages([]);
    call.clearRechargesCache();

    if (phoneError) {
      setPhoneError('');
    }
  };

  const onSubmitPhone = (): boolean => {
    const currentError = validatePhone(phone);

    if (currentError) {
      setPhoneError(currentError);
      return false;
    }

    setPhone(onlyDigits(phone));
    setPhoneError('');
    return true;
  };

  const onPackageSelect = (selectedTopUpPackage: TopUpPackage) => {
    setSelectedPackage(selectedTopUpPackage);
  };

  const onPackagesLoad = (loadedPackages: TopUpPackage[]) => {
    setPackages(loadedPackages);
    setSelectedPackage(null);
  };

  const onPaymentChange = (field: keyof PaymentData, value: string) => {
    if (field === 'cardnumber') {
      setPayment((previous) => ({ ...previous, cardnumber: formatCardNumber(value) }));
      return;
    }

    if (field === 'expiry') {
      setPayment((previous) => ({ ...previous, expiry: formatExpiry(value) }));
      return;
    }

    if (field === 'cvv') {
      setPayment((previous) => ({ ...previous, cvv: onlyDigits(value).slice(0, 3) }));
      return;
    }

    setPayment((previous) => ({ ...previous, [field]: value }));
  };

  const onPaymentCompleted = (generatedTopUpNumber: string, generatedTopUpDate: string) => {
    setTopUpNumber(generatedTopUpNumber);
    setTopUpDate(generatedTopUpDate);
  };

  const onResetFlow = () => {
    setPhone('');
    setPhoneError('');
    setSelectedPackage(null);
    setPackages([]);
    setPayment(INITIAL_PAYMENT);
    setTopUpNumber('');
    setTopUpDate('');
    call.clearRechargesCache();
  };

  const hasValidPhone = onlyDigits(phone).length === 10;
  const hasPackages = packages.length > 0;

  return (
    <BrowserRouter>
      <PortalNavigation />
      <PortalLayout>
        <Routes>
          <Route
            path={ROUTES.LANDING}
            element={
              <Landing
                phone={formatPhoneInput(phone)}
                phoneError={phoneError}
                onPhoneChange={onPhoneChange}
                onSubmitPhone={onSubmitPhone}
                onPackagesLoad={onPackagesLoad}
              />
            }
          />

          <Route
            path={ROUTES.RECHARGES}
            element={
              hasValidPhone && hasPackages ? (
                <Recharges packages={packages} onPackageSelect={onPackageSelect} />
              ) : (
                <Navigate to={ROUTES.LANDING} replace />
              )
            }
          />

          <Route
            path={ROUTES.PAY_WITH_CARD}
            element={
              hasValidPhone && selectedPackage ? (
                <PayWithCard
                  phone={phone}
                  selectedPackage={selectedPackage}
                  payment={payment}
                  onPaymentChange={onPaymentChange}
                  onPaymentCompleted={onPaymentCompleted}
                />
              ) : (
                <Navigate to={ROUTES.RECHARGES} replace />
              )
            }
          />

          <Route
            path={ROUTES.PAYMENTS}
            element={
              selectedPackage && topUpNumber ? (
                <Payments
                  topUpNumber={topUpNumber}
                  topUpDate={topUpDate}
                  receiptDetail={selectedPackage.receipt}
                  phone={phone}
                />
              ) : (
                <Navigate to={ROUTES.LANDING} replace />
              )
            }
          />

          <Route path={ROUTES.ERROR} element={<ErrorView onResetFlow={onResetFlow} />} />
          <Route path="*" element={<Navigate to={ROUTES.LANDING} replace />} />
        </Routes>
      </PortalLayout>
    </BrowserRouter>
  );
}

export default App;
